/**
 * ARTMiE per-visitor re-ranking (the "apply" step of personalization).
 * For each recommendation section — the native PDP rows, the cart drawer, and the homepage grid — it
 * asks the engine to re-order THIS visitor's shown products (the trained LambdaMART model, with the
 * visitor's own click/cart history as features) and reorders the cards to match. Where no RANKING model
 * is deployed for a placement the engine answers fallback:true and the order is left untouched.
 * Consent-gated: only personalizes for visitors who allowed analytics.
 *
 * Reads window.AMH_CART_RECO = { market, apiBase }.
 */
(function () {
  "use strict";
  var CFG = window.AMH_CART_RECO || {};
  var MARKET = String(CFG.market || "").trim().toUpperCase();
  var API = String(CFG.apiBase || "").replace(/\/+$/, "");
  if (!MARKET || !API) return;

  // Native recommendation rows, the cart drawer, and the homepage product grid (Horizon `product-list`
  // section — its id is per-store: `__plist_bestsellers` on SK, `__product_list_*` elsewhere).
  var REC_SEL = '[id*="recommended_products"], [id*="alternative_products"], [data-amh-cc="1"], [id*="__plist_bestsellers"], [id*="__product_list_"]';

  var _sid = null;
  function sid() { if (_sid) return _sid; try { var k = "amh_sid", v = localStorage.getItem(k); if (!v) { v = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(k, v); } _sid = v; } catch (e) { _sid = "s_" + Math.random().toString(36).slice(2); } return _sid; }

  // Shopify's Customer Privacy API is not present until loaded — preload it so consentOk() below
  // can read the real analytics-consent state (rerank runs per section, after this resolves).
  try { if (!(window.Shopify && window.Shopify.customerPrivacy) && window.Shopify && typeof window.Shopify.loadFeatures === "function") window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function () {}); } catch (e) {}

  function consentOk() {
    try {
      var sp = window.Shopify && window.Shopify.customerPrivacy;
      if (sp && typeof sp.analyticsProcessingAllowed === "function") return !!sp.analyticsProcessingAllowed();
      if (typeof window.__artmieConsent === "function") return !!window.__artmieConsent("analytics");
      if (document.cookie.indexOf("artmie_consent=analytics") !== -1 || document.cookie.indexOf("artmie_consent=all") !== -1) return true;
    } catch (e) { /* ignore */ }
    return false;
  }

  function handleOf(a) { if (!a) return null; var h = (a.getAttribute("href") || "").split("/products/")[1]; return h ? h.split("?")[0].split("#")[0].replace(/\/$/, "") : null; }
  function isHome() { return /^\/([a-z]{2}(-[a-z]{2})?\/?)?$/.test(location.pathname); }
  // Which deployment governs this section. A product grid outside the homepage has no placement → skipped.
  function placementOf(sec) {
    if (sec.getAttribute && sec.getAttribute("data-amh-cc") === "1") return "cart";
    // getAttribute, never `.id`: a <form> whose id matches the selector has an <input name="id"> (the
    // variant id), and form.id then returns that INPUT, not a string — `id.indexOf` threw, the scan
    // aborted at that element, and every section after it in document order was never ranked
    // (2026-09-18: the PDP recommended row stayed in metafield order while the alternatives row ranked).
    var id = (sec.getAttribute && sec.getAttribute("id")) || "";
    if (id.indexOf("recommended_products") !== -1 || id.indexOf("alternative_products") !== -1) return "pdp";
    if (id.indexOf("__plist_bestsellers") !== -1 || id.indexOf("__product_list_") !== -1) return isHome() ? "homepage" : null;
    return null;
  }

  // The cards of a section are the DIRECT CHILDREN of one list container, and reordering means moving
  // those. "Closest card element" was the wrong unit: Horizon wraps every <product-card> in its own
  // div.resource-list__item, so each card had a different parent and appendChild silently moved nothing
  // (measured 2026-09-17 on the homepage grid: 12 cards, 12 distinct parents — the PDP rows are built
  // the same way). So: the container is the lowest common ancestor of two different products' links,
  // and each card is the ancestor of its link that sits directly under that container.
  function cardsIn(sec) {
    var links = [], seen = {};
    sec.querySelectorAll('a[href*="/products/"]').forEach(function (a) { var h = handleOf(a); if (h && !seen[h]) { seen[h] = a; links.push(h); } });
    if (links.length < 2) return { map: {}, order: [], container: null };
    var a1 = seen[links[0]], a2 = seen[links[1]], node = a1.parentNode, container = null;
    while (node && node !== document.body) { if (node.contains(a2)) { container = node; break; } node = node.parentNode; }
    if (!container) return { map: {}, order: [], container: null };
    var map = {}, order = [];
    links.forEach(function (h) {
      var el = seen[h];
      while (el && el.parentNode !== container) el = el.parentNode;
      if (el && el !== container) { map[h] = el; order.push(h); }
    });
    return { map: map, order: order, container: container };
  }

  // A section is ranked per CONTENT, not once per element: Horizon renders the PDP "recommended"
  // row, then swaps its cards in again after load, so an element ranked once (or skipped while it was
  // still empty) would keep its fresh, unranked cards forever (measured 2026-09-18: one engine call for
  // two rows, the recommended row never matched the engine). Remember the handle set AND the first
  // card element; a new element with the same handles is new content.
  var done = new WeakMap();
  function rerank(sec) {
    if (!consentOk()) return;
    var placement = placementOf(sec);
    if (!placement) return;
    var ci = cardsIn(sec);
    if (ci.order.length < 3) return;
    var sig = ci.order.slice().sort().join("|");
    var prev = done.get(sec);
    if (prev && prev.sig === sig && prev.el === ci.map[ci.order[0]]) return;
    done.set(sec, { sig: sig, el: ci.map[ci.order[0]] });
    fetch(API + "/api/public/reco/recommend", {
      method: "POST", headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ market: MARKET, placement: placement, poolHandles: ci.order, user: sid(), session: sid(), count: ci.order.length })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (!res || !res.ok || res.fallback || !Array.isArray(res.cards) || !res.cards.length) return; // no personalization active → leave as-is
        var ranked = res.cards.map(function (c) { return c.handle; }).filter(function (h) { return ci.map[h]; });
        if (ranked.length < 2) return;
        // Ranked cards first, in the engine's order; anything the engine did not return (e.g. a card it
        // could not resolve) keeps its relative order AFTER them — never in front of them.
        var rest = ci.order.filter(function (h) { return ranked.indexOf(h) === -1; });
        var container = ci.container;
        if (!container) return;
        ranked.concat(rest).forEach(function (h) { var card = ci.map[h]; if (card && card.parentNode === container) container.appendChild(card); });
        done.set(sec, { sig: sig, el: ci.map[ranked[0]] }); // our own reorder is not new content
      })
      .catch(function () { /* leave order untouched on any error */ });
  }

  // One odd element must never take the whole pass down with it.
  function scan() { document.querySelectorAll(REC_SEL).forEach(function (sec) { try { rerank(sec); } catch (e) { /* skip this element */ } }); }
  var t = null;
  function boot() {
    scan();
    try { new MutationObserver(function () { clearTimeout(t); t = setTimeout(scan, 300); }).observe(document.body, { childList: true, subtree: true }); } catch (e) { /* ignore */ }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
